<template lang="pug">
    li(
      :class="clss"
      @mouseover="hover"
      @mouseleave="blur"
    )
        .tree-item
          a(
            type="button"
            :href="href"
            :title="item.title"
            :target="target"
            v-touch:tap="trigger"
            @keyup.enter.space="trigger"
            @click.prevent.stop
            @mouseover="focus"
            @mouseleave="leave"
          ) {{ item.title }}

            //font-awesome-icon.down(v-if="children.length && ( ! maxDepth || depth > 1)" icon="chevron-down")  

            button(
              v-if="toggleEvent === 'click' && children && children.length && ( ! maxDepth || depth < maxDepth)"
              @click="toggle"
            )
              i.is-open(v-if="active")
              i.is-closed(v-else)
        TreeMenu(
          v-if="children.length && ( ! maxDepth || depth < maxDepth)"
          :items="children" :depth="depth + 1" :max-depth="maxDepth"
          :toggle-event="toggleEvent"
          :show="active"
        )

</template>

<script>
import { get } from '@/helpers';
import router from '@/router';

import { mapGetters, mapActions } from 'vuex';

export default {
    name: 'TreeMenuItem',
    data() {
        return {
            timeout: undefined,
            visible: false,
            mouseover: false
        };
    },
    props: {
        item: {
            type: Object,
            required: true
        },
        depth: {
            type: Number,
            required: true
        },
        maxDepth: {
            type: Number
        },
        toggleEvent: {
            type: String,
            required: true
        },
        current: {
            type: Object
        },
        activeIds: {
            type: Array
        },
        delay: {
            type: Number,
            required: true
        },
        allowMultipleActiveItems: {
            type: Boolean,
            required: true
        },
        fullWidthThreshold: {
            type: Number
        },
        font: {
            type: String
        }
    },
    computed: {
        active() {
            if (this.allowMultipleActiveItems === true) return this.visible;
            return this.visible && this.current && this.item.id === this.current.id;
        },
        focused() {
            if (this.allowMultipleActiveItems === true) return this.mouseover;
            return (this.mouseover || this.active) && this.current && this.item.id === this.current.id;
        },
        folder() {
            const view = get(this.item, 'page.view');
            if (view === 'Folder') return true;
            return false;
        },
        redirect() {
            const view = get(this.item, 'page.view');
            if (view === 'Redirect' && this.hyperlink.type !== 'internal' && this.hyperlink.href) return true;
            return false;
        },
        hyperlink() {
            return get(this.item, 'page.hyperlink') || {};
        },
        href() {
            let href = '#';

            if (this.redirect) {
                const link = this.hyperlink.href;
                switch (this.hyperlink.type) {
                    case 'external':
                        if (!link.startsWith('https://') && !link.startsWith('http://')) href = `https://${link}`;
                        else href = link;
                        break;

                    case 'mailto':
                        href = `mailto:${link}`;
                        break;

                    case 'tel':
                        href = `tel:${link}`;
                        break;
                }
            } else if (!this.folder && this.item && this.item.path) {
                href = this.item.path;
            }

            return href;
        },
        target() {
            let target = '_self';
            if (this.hyperlink.type === 'external') {
                target = get(this.hyperlink, 'target') || '_self';
            }
            return target;
        },
        children() {
            let children = [];
            if (this.item.children && this.item.children.length) {
                children = this.item.children.filter(item => {
                    let visible = true;

                    if (item.restriction === 'members' && !this.isAuthenticated) visible = false;
                    else if (item.restriction === 'guests' && this.isAuthenticated) visible = false;
                    if (item.disabled) visible = false;

                    return visible;
                });
            }

            return children;
        },
        clss() {
            let value = [];

            if (
                this.$route.path === this.item.path ||
                (this.activeIds && this.activeIds.length && this.activeIds.includes(this.item.id))
            )
                value.push('active');

            if (this.$route.path === this.item.path) value.push('active');
            if (this.active) value.push('visible');
            if (this.focused) value.push('hover');
            if (this.children.length) value.push('has-children');
            if (this.depth === 1 && this.children.length >= this.fullWidthThreshold) value.push('full-width');

            return value;
        },
        ...mapGetters({
            isAuthenticated: 'auth/isAuthenticated'
        })
    },
    methods: {
        hover() {
            if (this.timeout) clearTimeout(this.timeout);
            if (this.toggleEvent === 'hover') {
                this.$emit('open', this.item);
                this.visible = true;
            }
        },
        blur() {
            this.timeout = setTimeout(() => {
                if (this.toggleEvent === 'hover') {
                    this.$emit('close', this.item);
                    this.visible = false;
                }
            }, this.delay);
        },
        focus() {
            if (this.toggleEvent === 'hover') {
                this.$emit('focus', this.item);
                this.mouseover = true;
            }
        },
        leave() {
            if (this.toggleEvent === 'hover') {
                this.$emit('leave', this.item);
                this.mouseover = false;
            }
        },
        open(force = false) {
            if (this.toggleEvent === 'click' || force) {
                this.$emit('open', this.item);
                this.visible = true;
            }
        },
        close() {
            if (this.toggleEvent === 'click') {
                this.$emit('close', this.item);
                this.visible = false;
            }
        },
        toggle() {
            if (this.toggleEvent === 'click') {
                if (this.active) {
                    this.$emit('close', this.item);
                    this.visible = false;
                } else {
                    this.$emit('open', this.item);
                    this.visible = true;
                }
            }
        },
        trigger(event) {
            if (event.type === 'mouseup' && event.which !== 1) return;

            if (this.item.page.view === 'Logout') {
                this.signout();
            } else if (this.folder) {
                this.toggle();
            } else {
                let next = true;
                if (this.children && this.children.length && !this.active) {
                    next = false;
                    this.open(true);
                }

                if (next) {
                    if (this.redirect) {
                        const win = window.open(this.href, this.target);
                        win.focus();
                    } else {
                        router.push(this.item.path).catch(error => {
                            if (error.code !== 'MODULE_NOT_FOUND') console.error(error);
                        });
                    }
                }
            }
        },
        ...mapActions({
            signout: 'auth/signout'
        })
    }
};
</script>

<style lang="sass" scoped></style>
